import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { navigate, useStaticQuery, graphql } from "gatsby";
import { SearchContext } from "../context/SearchContext";
import { FilterContext } from "../context/FilterContext";
import { GetQuerysContext } from "../context/GetQuerysContext";
import { AuthUserContext } from "../context/AuthUser";
import { sendSearchHistory } from "../services/api";

const getFilters = (params) => {
  if (params) {
    const filter = Object.fromEntries(params?.entries());

    return { ...filter };
  }
};

const MenuSearch = ({ user, handleOpenSearch }) => {
  const getDataSuggestions = useStaticQuery(graphql`
    query {
      allGoogleSheetWordToFilter {
        nodes {
          search
          result
        }
      }
    }
  `);

  const { AllQuery } = useContext(GetQuerysContext);
  const { setKeySearch, keySearch } = useContext(SearchContext);
  const { setDataProductsFilter } = useContext(FilterContext);
  const { authUser } = useContext(AuthUserContext);

  const [inputValue, setInputValue] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const options = useMemo(() =>
    getDataSuggestions.allGoogleSheetWordToFilter.nodes.map(x => ({
      value: x.result,
      label: x.search,
      result: x.result,
      name: x.search
    })), [getDataSuggestions]
  );

  const filterSuggestions = (value) => {
    if (!value.trim()) return [];

    const words = value.trim().split(/\s+/);
    const lastWord = words[words.length - 1];
    
    if (!lastWord) return [];

    const filtered = options.filter(option =>
      option.label.toLowerCase().includes(lastWord.toLowerCase()) ||
      option.result.toLowerCase().includes(lastWord.toLowerCase())
    );

    return filtered.slice(0, 10); 
  };

  const addKeyword = (keyword) => {
 
    const keywordObj = {
      id: Date.now() + Math.random(),
      value: keyword.result || keyword.toLowerCase(),
      label: keyword.label || keyword,
      result: keyword.result || keyword.toLowerCase(),
      name: keyword.label || keyword
    };

    const exists = selectedKeywords.some(k =>
      k.value === keywordObj.value || k.label === keywordObj.label
    );

    if (!exists) {
      setSelectedKeywords(prev => [...prev, keywordObj]);
      localStorage.setItem("searchTerm", keywordObj.result);
    }
  };

  const removeKeyword = (keywordId) => {
    setSelectedKeywords(prev => prev.filter(k => k.id !== keywordId));
  };

  const processAutoComplete = (text) => {
    if (!text.trim()) return;

    const words = text.trim().split(/\s+/);
    let processedText = text;
    let foundMatches = [];

    for (let length = Math.min(words.length, 4); length >= 1; length--) {
      for (let start = 0; start <= words.length - length; start++) {
        const phrase = words.slice(start, start + length).join(' ');
        
        const match = options.find(option =>
          option.label.toLowerCase() === phrase.toLowerCase() ||
          option.result.toLowerCase() === phrase.toLowerCase()
        );

        if (match) {
          const isAlreadyProcessed = foundMatches.some(fm => 
            fm.start <= start && fm.end >= start + length - 1
          );

          if (!isAlreadyProcessed) {
            foundMatches.push({
              match,
              phrase,
              start,
              end: start + length - 1,
              length
            });
          }
        }
      }
    }

    foundMatches.sort((a, b) => a.start - b.start);

    if (foundMatches.length > 0) {
      let remainingText = '';
      let lastProcessedIndex = 0;

      foundMatches.forEach(({ match, start, end }) => {
        if (start > lastProcessedIndex) {
          const freeText = words.slice(lastProcessedIndex, start).join(' ');
          if (freeText.trim()) {
            remainingText += (remainingText ? ' ' : '') + freeText;
          }
        }

        addKeyword(match);
        lastProcessedIndex = end + 1;
      });

      if (lastProcessedIndex < words.length) {
        const freeText = words.slice(lastProcessedIndex).join(' ');
        if (freeText.trim()) {
          remainingText += (remainingText ? ' ' : '') + freeText;
        }
      }

      setInputValue(remainingText);
      return true; 
    }

    return false; 
  };

  const processWordsBeforeSearch = (text) => {
    if (!text.trim()) return { processedKeywords: [], remainingText: '' };

    const words = text.trim().split(/\s+/);
    let processedKeywords = [];
    let remainingWords = [];

    words.forEach(word => {
      const match = options.find(option =>
        option.label.toLowerCase() === word.toLowerCase() ||
        option.result.toLowerCase() === word.toLowerCase()
      );

      if (match) {
        processedKeywords.push(match.result);
      } else {
        remainingWords.push(word);
      }
    });

    return {
      processedKeywords,
      remainingText: remainingWords.join(' ')
    };
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    const filtered = filterSuggestions(value);
    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0 && value.trim().length > 0);
    setActiveSuggestionIndex(-1);


  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
        addKeyword(suggestions[activeSuggestionIndex]);
        setInputValue("");
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      } else if (inputValue.trim()) {
        const exactMatch = options.find(option => 
          option.label.toLowerCase() === inputValue.trim().toLowerCase() ||
          option.result.toLowerCase() === inputValue.trim().toLowerCase()
        );
        
        if (exactMatch) {
          addKeyword(exactMatch);
          setInputValue("");
          setShowSuggestions(false);
          setActiveSuggestionIndex(-1);
        } else {
          executeSearchWithText(inputValue.trim());
        }
      } else if (selectedKeywords.length > 0) {
        executeSearch(e);
      }
    } else if (e.key === ' ') {
      if (inputValue.trim()) {
        e.preventDefault(); 
        
        const processed = processAutoComplete(inputValue.trim());
        
        if (!processed) {
          setInputValue(prev => prev + ' ');
        }
        
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    } else if (e.key === 'Backspace' && !inputValue && selectedKeywords.length > 0) {
      const lastKeyword = selectedKeywords[selectedKeywords.length - 1];
      removeKeyword(lastKeyword.id);
    }
  };

  const selectSuggestion = (suggestion) => {
    const words = inputValue.trim().split(/\s+/);
    
    if (words.length > 1) {
      const remainingWords = words.slice(0, -1).join(' ');
      setInputValue(remainingWords + ' ');
    } else {
      setInputValue("");
    }
    
    addKeyword(suggestion);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    const allSuggestions = options.slice(0, 10);
    setShowSuggestions(allSuggestions.length > 0);
    setActiveSuggestionIndex(-1);
  }, [options]);

  useEffect(() => {
    setKeySearch({ nameKey: "", tagKey: [], tagButton: [] });
    localStorage.setItem("searchTerm", "");

    return () => {
      setDataProductsFilter([]);
    };
  }, []);

  const params = useMemo(() => new URLSearchParams(AllQuery), [AllQuery]);
  const [filter, setFilter] = useState(getFilters(params));
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const updateURL = (newValues) => {
    Object.keys(newValues).forEach((key) => {
      let value = newValues[key];
      if (Array.isArray(value)) {
        value = value.join(",");
      }
      if (!value) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    navigate(`/products/?${params.toString()}`);
  };

  const onChangeFilterMenu = async (e, filter, searchDisplayName, keywordsParamArg) => {
    e.preventDefault();

    updateURL(filter);
    setFilter(filter);
    
    let formattedSearchKey = "";
    if (searchDisplayName) {
      
      const displayNames = [];
      if (selectedKeywords.length > 0) {
        displayNames.push(...selectedKeywords.map(k => k.name));
      }

      const keywordNames = selectedKeywords.map(k => k.name).join(" ");
      const remainingText = searchDisplayName.replace(keywordNames, "").trim();
      if (remainingText) {
        displayNames.push(remainingText);
      }
      formattedSearchKey = displayNames.join(", ");
      
    } else if (keySearch.nameKey) {
      formattedSearchKey = keySearch.nameKey.trim().replace(/\s+/g, ', ');
      
    }
    // commented on by the temp url
    // sendSearchHistory(localStorage.getItem("token"), {
    //   Date: new Date().toLocaleString(),
    //   user: user || authUser?.user?.username || authUser?.user?.email || "",
    //   search_key: formattedSearchKey,
    //   keywords: keywordsParamArg || filter?.keywords || "",
    // }).catch(() => { });
  };

  const executeSearch = (e) => {
    e.preventDefault();

    const allKeywords = [];

    const selectedKeywordResults = selectedKeywords.map(item => item.result).filter(Boolean);
    allKeywords.push(...selectedKeywordResults);

    if (inputValue.trim()) {
      const { processedKeywords, remainingText } = processWordsBeforeSearch(inputValue.trim());
      
      allKeywords.push(...processedKeywords);
      
      if (remainingText) {
        const textWords = remainingText.split(/\s+/).filter(word => word.length > 0);
        const textKeywords = textWords.map(word => word.toLowerCase().trim());
        allKeywords.push(...textKeywords);
      }
    }

    if (allKeywords.length === 0) {
      return;
    }

    const keywordsParam = allKeywords.join("_");

    const displayNames = [];
    if (selectedKeywords.length > 0) {
      displayNames.push(...selectedKeywords.map(k => k.name));
    }
    if (inputValue.trim()) {
      displayNames.push(inputValue.trim());
    }

    const displayName = displayNames.join(" ");
    setKeySearch({
      nameKey: displayName,
      tagKey: [],
      tagButton: []
    });

    onChangeFilterMenu(e, {
      ...filter,
      keywords: keywordsParam, 
      by_fabric: "",
      collection: "",
      stockoperationorbyorder: "",
      sustainable: "",
    }, displayName, keywordsParam);

    setInputValue("");
    setSelectedKeywords([]);
    handleOpenSearch();
  };

  const executeSearchWithText = (searchText) => {
    const trimmedText = searchText.trim();

    if (!trimmedText) {
      return;
    }

    const allKeywords = [];

    const selectedKeywordResults = selectedKeywords.map(item => item.result).filter(Boolean);
    allKeywords.push(...selectedKeywordResults);

    const { processedKeywords, remainingText } = processWordsBeforeSearch(trimmedText);
    
    allKeywords.push(...processedKeywords);
    
    if (remainingText) {
      const textWords = remainingText.split(/\s+/).filter(word => word.length > 0);
      const textKeywords = textWords.map(word => word.toLowerCase().trim());
      allKeywords.push(...textKeywords);
    }

    const keywordsParam = allKeywords.join("_");

    const displayNames = [];
    if (selectedKeywords.length > 0) {
      displayNames.push(...selectedKeywords.map(k => k.name));
    }
    displayNames.push(trimmedText);

    const displayNameText = displayNames.join(" ");
    setKeySearch({
      nameKey: displayNameText,
      tagKey: [],
      tagButton: []
    });

    const syntheticEvent = { preventDefault: () => { } };
    onChangeFilterMenu(syntheticEvent, {
      ...filter,
      keywords: keywordsParam, 
      by_fabric: "",
      collection: "",
      stockoperationorbyorder: "",
      sustainable: "",
    }, displayNameText, keywordsParam);

    setInputValue("");
    setSelectedKeywords([]);
    handleOpenSearch();
  };

  return (
    <>
      <div className="search-container showSearch" id="search-container">
        <div
          className="search-backdrop-content"
          onClick={() => handleOpenSearch()}
        ></div>
        <form onSubmit={executeSearch} className="search-box">
          <div className="search-box__container">
            <div className="custom-search-input" ref={suggestionsRef}>
              <div className="keywords-container">
                {selectedKeywords.length > 0 && (
                  <div className="selected-keywords-text">
                    {selectedKeywords.map((keyword, index) => (
                      <span key={keyword.id}>
                        {keyword.label}
                        <span
                          onClick={() => removeKeyword(keyword.id)}
                          style={{
                            marginLeft: '4px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                          aria-label={`Remove ${keyword.label}`}
                        >
                          ×
                        </span>
                        {/* {index < selectedKeywords.length - 1 && ', '} */}
                      </span>
                    ))}
                    {inputValue.trim() && ' '}
                  </div>
                )}

              
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={selectedKeywords.length === 0 ? "Type and choose keyword" : ""}
                  className="search-input"
                  autoComplete="off"
                />
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={`${suggestion.value}-${index}`}
                      className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''
                        }`}
                      onClick={() => selectSuggestion(suggestion)}
                      onMouseEnter={() => setActiveSuggestionIndex(index)}
                    >
                      <span className="suggestion-label">{suggestion.label}</span>
                       {/* {suggestion.result !== suggestion.label && (
                        <span className="suggestion-result">→ {suggestion.result}</span>
                      )} */}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              disabled={selectedKeywords.length === 0 && !inputValue.trim()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (inputValue.trim()) {
                  executeSearchWithText(inputValue.trim());
                } else if (selectedKeywords.length > 0) {
                  executeSearch(e);
                }
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                  fill="currentColor"
                />
              </svg>
            </button>


          </div>
        </form>
      </div>

      <style jsx>{`
        .custom-search-input {
          position: relative;
          flex: 1;
          min-width: 0;
        }

        .keywords-container {
          display: flex;
          flex-wrap: wrap;
          align-items: center;                         
          border-radius: 0;
          background-color: white;
          min-height: 48px;
          cursor: text;
          padding-left: 5px!important;
        }
        .keywords-container input{
            min-height: 55px;
            padding-left: 5px;
        }

        .keywords-container:focus-within {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .selected-keywords-text {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          font-size: 16px;
          color: #333;
          position: relative;
        }
        .selected-keywords-text span{
          background: #dddddd;
          font-size: 12px;
          border-radius: 5px;
          padding: 0 3px;
          margin-right: 3px;
        }
        .search-input {
          border: none;
          outline: none;
          flex: 1;
          min-width: 120px;
          font-size: 16px;
          padding: 4px 0;
          background: transparent;
        }

        .search-input::placeholder {
          color: #6c757d;
        }

        .suggestions-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          margin-top: 4px;
        }

        .suggestion-item {
          padding: 12px 16px;
          cursor: pointer;
          border-bottom: 1px solid #f8f9fa;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
        }

        .suggestion-item:last-child {
          border-bottom: none;
        }

        .suggestion-item:hover,
        .suggestion-item.active {
          background-color: #f8f9fa;
        }

        .suggestion-label {
          font-weight: 500;
          color: #333;
        }

        .suggestion-result {
          color: #6c757d;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .keywords-container {
            padding: 6px 8px;
            min-height: 44px;
          }

          .search-input {
            font-size: 16px; /* Evitar zoom en iOS */
          }

          .suggestion-item {
            padding: 10px 12px;
          }
        }
      `}</style>
    </>
  );
};

export default MenuSearch;
